import { ChevronDown, Download } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { AmbientVideo } from "@/components/marketing/AmbientVideo";
import { WaitlistModal } from "@/components/marketing/WaitlistModal";
import { Button } from "@/components/ui/Button";
import WaveText from "@/components/ui/wave-text";
import { appSignInUrl, env } from "@/lib/env";
import { preserveSearch } from "@/lib/nav";
import { useDesktopPlatform } from "@/lib/useDesktopPlatform";
import ChatInput from "./ChatInput";

const Hero = () => {
  const { t } = useTranslation();
  const showDesktopDownload = useDesktopPlatform() !== "unknown";

  // Production is launched: the typed prompt hands into app sign-in. The
  // waitlist modal remains available to explicit staging/local preview mode.
  const [modalOpen, setModalOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("ref") ?? undefined;

  const handleSend = (message: string) => {
    if (env.launched) {
      // A referral link used to sign up must count as a referral, not just one
      // used to join the waitlist (which WaitlistForm.tsx captures separately).
      window.location.assign(appSignInUrl(message, referralCode));
      return;
    }
    setModalOpen(true);
  };

  // i18n returns a fresh array each render; freeze it so the typewriter isn't reset.
  const prompts = useMemo(() => t("hero.chat.prompts", { returnObjects: true }) as string[], [t]);

  return (
    <section className="relative flex min-h-svh w-full flex-col items-center justify-center overflow-hidden px-4 pb-8 pt-28 sm:pb-24 sm:pt-32 md:px-8 md:pt-36 lg:snap-start">
      <AmbientVideo src="/videos/hero-loop.mp4" poster="/videos/hero-poster.webp" />

      {/* Soft monochrome bloom behind the headline */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-[1] opacity-20 mix-blend-screen"
        style={{
          backgroundImage:
            "radial-gradient(80% 60% at 50% 40%, rgb(255 255 255 / 0.55) 0%, transparent 70%)",
        }}
      />

      {/* Fade the video into the page canvas */}
      <div
        aria-hidden
        className="absolute inset-x-0 bottom-0 z-[1] h-40 bg-gradient-to-t from-background to-transparent"
      />

      <div className="relative z-[2] flex w-full max-w-[760px] flex-col items-center">
        {/* Hero-only eyebrow: plain sans micro-label (no pill, no mono) so the
            headline stack reads softer than the section labels below. */}
        <span className="mb-5 inline-flex w-fit items-center gap-2 font-sans text-eyebrow font-medium uppercase text-subtle-foreground">
          <span
            aria-hidden
            className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-primary"
          />
          {t("hero.eyebrow")}
        </span>

        <h1 className="mb-4 text-balance text-center font-sans text-[clamp(2.25rem,3.4vw,3rem)] font-semibold leading-[1.08] tracking-tight text-foreground">
          <span className="block">
            <WaveText text={t("hero.titleSetup")} yOffset={12} />
          </span>
          <span className="block">
            <WaveText text={t("hero.titleLead")} delay={0.42} yOffset={12} />{" "}
            <span className="text-primary">
              <WaveText text={t("hero.titleAccent")} delay={0.72} yOffset={12} />
            </span>
          </span>
        </h1>

        <p className="mb-7 max-w-lg text-balance text-center text-sm leading-relaxed text-muted-foreground sm:mb-9 sm:text-base">
          <WaveText text={t("hero.subtitle")} byWord staggerDelay={0.014} yOffset={12} />
        </p>

        <div className="w-full">
          <ChatInput
            onSend={handleSend}
            prefix={t("hero.chat.promptPrefix")}
            phrases={prompts}
            label={t("hero.chat.inputLabel")}
            sendLabel={t("hero.chat.send")}
            attachLabel={t("hero.chat.attach")}
            voiceLabel={t("hero.chat.voice")}
          />
        </div>
        {env.launched ? (
          <div className="mt-6 flex w-full flex-col items-center gap-3">
            <div className="flex w-full max-w-sm flex-col gap-3 sm:max-w-none sm:flex-row sm:justify-center">
              {showDesktopDownload ? (
                <Button
                  className="h-auto min-h-11 whitespace-normal py-3 text-center sm:py-2"
                  to={preserveSearch("/download", searchParams.toString())}
                  leading={<Download className="h-4 w-4" aria-hidden />}
                >
                  {t("nav.downloadDesktop")}
                </Button>
              ) : null}
              <Button
                variant={showDesktopDownload ? "secondary" : "primary"}
                href={appSignInUrl("", referralCode)}
                className="h-auto min-h-11 whitespace-normal py-3 text-center sm:py-2"
              >
                {t("download.openWeb")}
              </Button>
            </div>
            {showDesktopDownload ? (
              <p className="text-center text-xs text-muted-foreground">{t("download.platforms")}</p>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* The hero fills the viewport, so tell first-time visitors the page continues. */}
      <button
        type="button"
        aria-label={t("hero.scrollCue")}
        onClick={() => document.getElementById("solutions")?.scrollIntoView({ behavior: "smooth" })}
        className="relative z-[2] mt-8 flex shrink-0 cursor-pointer flex-col items-center gap-1.5 text-subtle-foreground transition-colors duration-300 hover:text-foreground sm:absolute sm:bottom-6 sm:left-1/2 sm:mt-0 sm:-translate-x-1/2"
      >
        <span className="font-mono text-eyebrow uppercase">{t("hero.scrollCue")}</span>
        <ChevronDown className="h-4 w-4 animate-bounce" aria-hidden />
      </button>

      <WaitlistModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </section>
  );
};

export default Hero;
